import type {
  PropertyFilterOption,
  PropertyFilterQuery,
} from '@cloudscape-design/collection-hooks';
import type { NonCancelableCustomEvent } from '@risk-smart/themed-cloudscape-components/interfaces';
import type { PropertyFilterProps } from '@risk-smart/themed-cloudscape-components/property-filter';
import { defaultPropertyFilterI18nStrings } from '@risksmart-app/components/src/table/propertyFilterI18nStrings';
import type { DataSourceType } from '@risksmart-app/shared/reporting/schema';
import { type FC, useRef, useState } from 'react';
import type { StatusType } from 'src/components/form/controlled-select/SelectUtils';
import PropertyFilterPanel from 'src/components/property-filter-panel';

import { useLazyGetReportingFilterOptions } from '@/hooks/queries';

import { useCustomDatasourceHelpers } from '../useCustomDatasourceHelpers';
import type { FieldDefinitionWithDataSourceIndex } from './customDatasourceModel';
import { displayTypes } from './display-types';
import { getFieldFromUniqueId } from './fieldValue';
import type { RelatedDataSource } from './types';

const limit = 50;

export type Props = {
  query: PropertyFilterQuery;
  onChange: (query: PropertyFilterQuery) => void;
  allFields: FieldDefinitionWithDataSourceIndex[];
  datasources: RelatedDataSource[];
  /** Render as read-only pills for the dashboard widget filter preview. */
  disabled?: boolean;
};

const CustomDatasourcePropertyFilter: FC<Props> = ({
  allFields,
  datasources,
  query,
  onChange,
  disabled,
}) => {
  const pageNumber = useRef<number>(0);
  const [filteringOptions, setFilteringOptions] = useState<
    PropertyFilterOption[]
  >([]);
  const request = useRef<
    | {
        filteringProperty: PropertyFilterProps.FilteringProperty | undefined;
        filteringText: string;
      }
    | undefined
  >(undefined);

  const [getFilterOptions] = useLazyGetReportingFilterOptions();
  const [status, setStatus] = useState<StatusType>('pending');

  const helpers = useCustomDatasourceHelpers();

  const fetchData = async ({
    filteringText,
    filteringProperty,
    fieldId,
    dataSourceType,
  }: {
    filteringText: string;
    filteringProperty: PropertyFilterProps.FilteringProperty;
    fieldId: string;
    dataSourceType: DataSourceType;
  }) => {
    try {
      const { data, error } = await getFilterOptions({
        dataSourceType,
        fieldId,
        filteringText,
        limit,
        offset: (pageNumber.current - 1) * limit,
      });
      // Both the tRPC and GraphQL (flag-off) lazy paths surface failures on the
      // returned result (`{ error }`) rather than throwing, so check it
      // explicitly to set the error status.
      if (error) {
        setStatus('error');

        return;
      }
      const items: PropertyFilterOption[] = data?.reportingFilterOptions
        .map(({ value }) => ({
          value,
          label: value,
          propertyKey: filteringProperty.key,
        }))
        .concat([
          { value: 'null', label: 'Blank', propertyKey: filteringProperty.key },
        ]) ?? [
        { value: 'null', label: 'Blank', propertyKey: filteringProperty.key },
      ];

      const hasNext = items.length === limit;
      if (
        !request.current ||
        request.current.filteringText !== filteringText ||
        request.current.filteringProperty !== filteringProperty
      ) {
        // there is another request in progress, discard the result of this one
        return;
      }
      setFilteringOptions((previousOptions) =>
        pageNumber.current === 1 ? items : previousOptions.concat(items)
      );
      setStatus(hasNext ? 'pending' : 'finished');
    } catch {
      setStatus('error');
    }
  };

  const handleLoadItems = ({
    detail: { filteringProperty, filteringText, firstPage, samePage },
  }: NonCancelableCustomEvent<PropertyFilterProps.LoadItemsDetail>) => {
    if (firstPage) {
      pageNumber.current = 0;
      // Only clear when the property changes, so options from the previous
      // property can't leak into the new dropdown; otherwise keep results
      // visible while the next page loads (RSP-5769).
      if (request.current?.filteringProperty?.key !== filteringProperty?.key) {
        setFilteringOptions([]);
      }
    }
    if (!filteringProperty) {
      return;
    }
    const fieldKeys = getFieldFromUniqueId(filteringProperty.key);

    // Match on dataSourceIndex too: duplicate fieldIds across datasources (tags,
    // departments, createdBy, …) would otherwise resolve to the first datasource
    // and load suggestions from the wrong dataset (RSP-4279).
    const field = allFields.find(
      (f) =>
        f.dataSourceIndex === fieldKeys.dataSourceIndex &&
        f.fieldId === fieldKeys.fieldId
    );
    if (!field) {
      return;
    }
    const fieldType = displayTypes[field.displayType];
    if (!fieldType.asyncOptionSuggestions) {
      return;
    }
    const dataSourceType = datasources[field.dataSourceIndex]?.type;

    if (!dataSourceType) {
      throw new Error('Data source not found');
    }

    setStatus('loading');
    if (!samePage) {
      pageNumber.current++;
    }
    request.current = {
      filteringProperty,
      filteringText,
    };

    fetchData({
      filteringText,
      filteringProperty,
      fieldId: fieldKeys.fieldId,
      dataSourceType,
    });
  };

  return (
    <PropertyFilterPanel
      disabled={disabled}
      // An empty node suppresses Cloudscape's clear-all button (a dead pill on a
      // disabled filter); undefined keeps the normal "Clear filters" when editing.
      customFilterActions={disabled ? <></> : undefined}
      filteringStatusType={status}
      onLoadItems={handleLoadItems}
      disableFreeTextFiltering={true}
      virtualScroll={true}
      expandToViewport={true}
      enableTokenGroups={true}
      i18nStrings={defaultPropertyFilterI18nStrings}
      query={query}
      filteringOptions={filteringOptions}
      filteringProperties={
        allFields
          .filter((fieldDef) => {
            return !!displayTypes[fieldDef.displayType].propertyConfig;
          })
          .map((fieldDef) => {
            return displayTypes[fieldDef.displayType].propertyConfig!(
              {
                ...fieldDef,
                key: fieldDef.value,
                propertyLabel: fieldDef.label ?? fieldDef.defaultNestedLabel,
                groupValuesLabel: '',
              },
              helpers,
              // custom data source filtering does not use any of these
              { departmentTypes: [], users: [], tagTypes: [], userGroups: [] }
            );
          }) ?? []
      }
      onChange={(e) => onChange(e.detail)}
    />
  );
};

export default CustomDatasourcePropertyFilter;
